/**
 * A verdict per pair of competing pages, and the "Cannibalization" card that shows it on `/review`
 * (quality sprint 7, workstream B). The pairs and their evidence are A's (`competing-pages.ts`);
 * this file decides what to do about each one and words it, for the card, the agent export and the
 * MCP alike.
 *
 * - **Merge**: one page wins the searches both pages show for, and the other adds nothing of its
 *   own: every search it earns (`CompetingOwnSearches`) the winner or a third live page of the site
 *   also shows for, so folding it in loses no search. Two copies of one page (`duplicate`) always
 *   merge. A merge also needs the pair to compete on its overlap (`competesOnOverlap`): a pair that
 *   competes only because one page holds the other's own search shows one search in the wrong
 *   place, not two pages doing one job.
 * - **Differentiate** (both pages compete, the merge test fails): **never a card** (round 3). Graded
 *   right 1 of 21 across `/review` and the Issues queue: the evidence says two pages share searches,
 *   not which change to make. The owner and the change (`differentiateChange`: the giving-up page's
 *   title or H1 carries the search) are still computed, for a reader of the shared-search list (F,
 *   near-miss searches); the everything export lists the pair as checked: "these pages share
 *   searches; we don't advise a change from this evidence". A pair where either page is merged into
 *   a third page is moot, and one where a page ranks for none of the shared searches splits nothing
 *   (orchestrator decision 2026-10-23, over the labelled set's two SlideSpeak pairs); each says so.
 * - **A merge needs a measured preference** (`mergeHasMeasuredPreference`): with Search Console the
 *   page that stays earns more of the clicks on the shared searches; impressions alone set it aside.
 * - **Keep both**: the pair shares searches but does not compete (`competes: false`). No card
 *   (owner, 2026-10-19: no card when there is nothing to act on); the everything export lists it
 *   as checked and fine.
 *
 * Which page stays on a merge: the split of the pair's clicks on the contested searches, or of
 * impressions when neither page has a click (where Google already sends the demand); without Search
 * Console, the better position weighted by estimated demand. Which page OWNS a shared search on a
 * differentiate: the one Google ranks better when it shows it (clicks first, then the
 * impression-weighted position, each search weighted by its demand), not the one shown on more days:
 * round 1's grade had a page shown often at about #27 handed a search from a page at #1 to #5. On a pair
 * that competes because one page holds the other's own comparison search, the page whose search it is
 * owns it: that search is what the page is for.
 *
 * One page is merged into one page. A page that would merge into two is merged into the one it
 * shares most with; its other pairs are set aside as moot.
 *
 * Every number on an estimated pair is labelled estimated. The line that says where the evidence
 * comes from is `cannibalizationEvidenceSourceLine`: the one place it is written. On estimates it is
 * the warning every estimated item ends with (`ESTIMATED_SEARCH_EVIDENCE_WARNING`, workstream E).
 */

import {
  COMPETING_SEARCH_MAX_POSITION,
  competingSearchWords,
  normalizeCompetingSearch,
  swapCompetingPair,
  type CompetingEvidenceSource,
  type CompetingPair,
  type CompetingPagesResult,
  type CompetingSharedSearch,
  type CompetingSide
} from './competing-pages';
import { ESTIMATED_SEARCH_EVIDENCE_WARNING } from './estimated-evidence';

export type CannibalizationVerdict = 'merge' | 'differentiate' | 'keep_both';

/** How the page that keeps (merge) or owns (differentiate) the shared searches was chosen. */
export type CannibalizationKeeperBasis =
  /** Search Console: more of the pair's clicks on the contested searches. */
  | 'clicks'
  /** Search Console: no clicks on either side, more of the impressions. */
  | 'impressions'
  /** Better positions on the contested searches, weighted by demand (estimates; or an exact tie). */
  | 'positions'
  /** The shared search is this page's own comparison search, held by the other page. */
  | 'own_search'
  /** The other page is merged into a third page, so this one keeps the shared searches. */
  | 'merged_elsewhere';

/** Why a competing pair gets no card (the everything export lists it as checked, with this). */
export type CannibalizationSetAside =
  /** One of the two pages is merged into a third page: its other pairs are moot. */
  | 'merged_elsewhere'
  /** One page ranks for none of the shared searches: there is nothing to split. */
  | 'one_page_not_ranking'
  /**
   * Search Console: a merge whose winner earns no more clicks on the shared searches than the other
   * page (`mergeHasMeasuredPreference`): impressions alone do not show which page Google prefers.
   */
  | 'no_click_preference'
  /**
   * Every differentiate verdict (round 3): not advised on the card or in the default export; the
   * everything export lists the pair with its shared searches.
   */
  | 'differentiate_not_advised';

/** The change a differentiate card names: the giving-up page's title or H1 carries the search. */
export interface CannibalizationChange {
  /** The shared search (the giving-up page's own wording of it). */
  search: string;
  /** The page whose title or H1 carries it (not the owner). */
  pageId: string;
  field: 'title' | 'H1' | 'title and H1';
  text: string;
}

/**
 * A competing pair as the card payload carries it: its contested searches only, the most demand
 * first and at most `CANNIBALIZATION_PAYLOAD_SEARCHES`, with the counts.
 */
export type CompetingPairShown = CompetingPair & {
  /** Every search the two pages share, contested or not. */
  sharedSearchCount: number;
  /** Every contested search (the list may be cut). */
  contestedSearchCount: number;
};

/**
 * Contested searches a pair carries in the payload. The card lists five; the estimated winner
 * weighs every one carried. On the sprint 7 frozen data the most a pair has is 17 (ManyRequests),
 * 2 (SlideSpeak); the cut keeps the payload of a large site small and drops only the smallest.
 */
export const CANNIBALIZATION_PAYLOAD_SEARCHES = 10;

/** A pair checked and found fine (keep both), for the everything export: page ids. */
export interface CompetingPairChecked {
  a: string;
  b: string;
  sharedSearchCount: number;
}

/**
 * What the review surface loads for a domain (`GET /urls/competing-pages`): every competing pair
 * (exclusions applied), and the pairs checked and found fine where both pages show for a shared
 * search no deeper than `COMPETING_SEARCH_MAX_POSITION` (the rest are passing appearances).
 */
export interface CompetingPagesPayload {
  source: CompetingEvidenceSource;
  /** The Search Console window the numbers cover; null on estimates. */
  window: { start: string; end: string } | null;
  /** Page id → URL, for every page in `pairs` or `checked`. */
  pages: Record<string, string>;
  pairs: CompetingPairShown[];
  checked: CompetingPairChecked[];
  /** Title and H1 of every page in `pairs`, for the change a differentiate card names. */
  texts: Record<string, { title: string | null; h1: string | null }>;
}

/** Both pages are seen no deeper than `COMPETING_SEARCH_MAX_POSITION` on this shared search. */
function bothShow(s: CompetingSharedSearch): boolean {
  const within = (side: CompetingSide) => side.position !== null && side.position <= COMPETING_SEARCH_MAX_POSITION;
  return !s.heldBy && within(s.a) && within(s.b);
}

/** The payload for a domain, from A's result. Pure. */
export function competingPagesPayload(
  result: CompetingPagesResult,
  window: { start: string; end: string } | null
): CompetingPagesPayload {
  const pages: Record<string, string> = {};
  const note = (p: CompetingPair) => {
    pages[p.a.pageId] = p.a.url;
    pages[p.b.pageId] = p.b.url;
  };
  const pairs: CompetingPairShown[] = result.pairs
    .filter((p) => p.competes)
    .map((p) => {
      note(p);
      const contested = p.sharedSearches.filter((s) => s.contested);
      return {
        ...p,
        sharedSearchCount: p.sharedSearches.length,
        contestedSearchCount: contested.length,
        sharedSearches: contested.slice(0, CANNIBALIZATION_PAYLOAD_SEARCHES)
      };
    });
  const checked: CompetingPairChecked[] = result.pairs
    .filter((p) => !p.competes && p.sharedSearches.some(bothShow))
    .map((p) => {
      note(p);
      return { a: p.a.pageId, b: p.b.pageId, sharedSearchCount: p.sharedSearches.length };
    });
  const texts: CompetingPagesPayload['texts'] = {};
  for (const p of pairs) {
    for (const id of [p.a.pageId, p.b.pageId]) texts[id] = result.pageTexts?.[id] ?? { title: null, h1: null };
  }
  return {
    source: result.source,
    window: result.source === 'search_console' ? window : null,
    pages,
    pairs,
    checked,
    texts
  };
}

export interface CompetingPairVerdict {
  pair: CompetingPairShown;
  verdict: Exclude<CannibalizationVerdict, 'keep_both'>;
  /** Merge: the page that stays. Differentiate: the page that should own the shared searches. */
  keeperId: string;
  /** Merge only: the page folded into the keeper. */
  mergedId: string | null;
  basis: CannibalizationKeeperBasis;
  /** Merged elsewhere: the page the other page is merged into. */
  mergedInto: string | null;
  /** Differentiate only: why the pair gets no card; null when it gets one. */
  setAside: CannibalizationSetAside | null;
  /** Differentiate with a card: the change it names. */
  change: CannibalizationChange | null;
}

const demandOf = (s: CompetingSide): number => s.impressions ?? s.searchVolume ?? 0;

/**
 * Which page Google prefers on the contested searches, and on what evidence. With
 * `impressions: false` (the owner of a shared search) the impression split is skipped: clicks, then
 * position.
 */
export function competingPairWinner(
  pair: CompetingPair,
  opts: { impressions: boolean } = { impressions: true }
): { id: string; basis: CannibalizationKeeperBasis } {
  const a = pair.a.pageId;
  const b = pair.b.pageId;
  const split = pair.split;
  if (split) {
    if (split.clicks !== null && split.clicks !== 0.5) return { id: split.clicks > 0.5 ? a : b, basis: 'clicks' };
    if (opts.impressions && split.impressions !== null && split.impressions !== 0.5) {
      return { id: split.impressions > 0.5 ? a : b, basis: 'impressions' };
    }
  }
  let scoreA = 0;
  let scoreB = 0;
  for (const s of pair.sharedSearches.filter((x) => x.contested)) {
    const pa = s.a.position ?? Infinity;
    const pb = s.b.position ?? Infinity;
    if (pa === pb) continue;
    // At least 1, so a search with no recorded demand still counts once.
    const weight = Math.max(demandOf(s.a), demandOf(s.b), 1);
    if (pa < pb) scoreA += weight;
    else scoreB += weight;
  }
  if (scoreA !== scoreB) return { id: scoreA > scoreB ? a : b, basis: 'positions' };
  const totalA = pair.ownSearches?.a.totalDemand ?? 0;
  const totalB = pair.ownSearches?.b.totalDemand ?? 0;
  if (totalA !== totalB) return { id: totalA > totalB ? a : b, basis: 'positions' };
  return { id: a < b ? a : b, basis: 'positions' };
}

const otherOf = (pair: CompetingPair, id: string): string => (pair.a.pageId === id ? pair.b.pageId : pair.a.pageId);

/** The pair seen from `id`, so the page is side `a`. */
function fromSide(pair: CompetingPairShown, id: string): CompetingPairShown {
  return pair.a.pageId === id
    ? pair
    : { ...swapCompetingPair(pair), sharedSearchCount: pair.sharedSearchCount, contestedSearchCount: pair.contestedSearchCount };
}

/** Every word of the search is in the text (`competingSearchWords`). */
function textTargets(text: string | null | undefined, search: string): boolean {
  if (!text) return false;
  const have = competingSearchWords(text);
  const want = [...competingSearchWords(search)];
  return want.length > 0 && want.every((w) => have.has(w));
}

/**
 * The change a differentiate card names: the first contested search (most demand first) that the
 * page giving it up targets in its title or H1, in that page's own wording. Null when there is none.
 */
function differentiateChange(
  pair: CompetingPairShown,
  ownerId: string,
  texts: CompetingPagesPayload['texts'] | undefined
): CannibalizationChange | null {
  const giver = otherOf(pair, ownerId);
  const t = texts?.[giver];
  if (!t) return null;
  for (const s of pair.sharedSearches.filter((x) => x.contested)) {
    const side = giver === pair.a.pageId ? s.a : s.b;
    for (const wording of [side.wording?.search ?? s.search, s.search]) {
      const inTitle = textTargets(t.title, wording);
      const inH1 = textTargets(t.h1, wording);
      if (inTitle && inH1) return { search: wording, pageId: giver, field: 'title and H1', text: t.title! };
      if (inTitle) return { search: wording, pageId: giver, field: 'title', text: t.title! };
      if (inH1) return { search: wording, pageId: giver, field: 'H1', text: t.h1! };
    }
  }
  return null;
}

/** One page ranks for none of the contested searches. */
function onePageNotRanking(pair: CompetingPair): boolean {
  const contested = pair.sharedSearches.filter((s) => s.contested);
  if (!contested.length) return false;
  return contested.every((s) => s.a.position === null) || contested.every((s) => s.b.position === null);
}

/**
 * A merge needs a measured preference (round 3). With Search Console, the page that stays must earn
 * more of the pair's clicks on the contested searches than the page folded in (`basis: 'clicks'`):
 * a merge resting on impressions with no click on either side is set aside. Without Search Console
 * the estimated merge stands (its warning is workstream E's).
 *
 * Evidence (sprint 7 frozen data; an independent grade of every queue pair item after workstream F
 * moved the Issues queue onto these verdicts, plus `/review`'s): with Search Console, every merge
 * whose winner earns clicks on the shared searches was graded good (3 of 3); merges on impressions
 * with no click on either side split 3 good and 3 wrong, the wrong ones folding a substantial page
 * with its own angle into a different job. Without Search Console (SlideSpeak) the one queue merge was
 * graded good. Telling a page's own angle apart is left open: a model check, not taken (owner
 * decision 2026-10-19).
 */
export function mergeHasMeasuredPreference(pair: CompetingPair, winner: { basis: CannibalizationKeeperBasis }): boolean {
  if (pair.source !== 'search_console') return true;
  return winner.basis === 'clicks';
}

/** The verdict for every competing pair of a domain. Pure, deterministic. */
export function competingPairVerdicts(
  pairs: readonly CompetingPairShown[],
  texts?: CompetingPagesPayload['texts']
): CompetingPairVerdict[] {
  const base = { mergedInto: null, setAside: null, change: null };
  const first: CompetingPairVerdict[] = pairs.map((pair) => {
    const winner = competingPairWinner(pair);
    const loser = otherOf(pair, winner.id);
    const loserOwn = pair.ownSearches ? (pair.a.pageId === loser ? pair.ownSearches.a : pair.ownSearches.b) : null;
    const merge = pair.duplicate || (pair.competesOnOverlap && loserOwn !== null && loserOwn.uncoveredCount === 0);
    if (merge) {
      return {
        ...base,
        pair,
        verdict: 'merge',
        keeperId: winner.id,
        mergedId: loser,
        basis: winner.basis,
        setAside: mergeHasMeasuredPreference(pair, winner) ? null : 'no_click_preference'
      };
    }
    const heldA = pair.ownSearchHeld.a && !pair.ownSearchHeld.b;
    const heldB = pair.ownSearchHeld.b && !pair.ownSearchHeld.a;
    if (heldA || heldB) {
      return { ...base, pair, verdict: 'differentiate', keeperId: heldA ? pair.a.pageId : pair.b.pageId, mergedId: null, basis: 'own_search' };
    }
    const owner = competingPairWinner(pair, { impressions: false });
    return { ...base, pair, verdict: 'differentiate', keeperId: owner.id, mergedId: null, basis: owner.basis };
  });

  // One page is merged into one page: the merge where it shares most of its own demand.
  const target = new Map<string, CompetingPairVerdict>();
  const shareOf = (v: CompetingPairVerdict) => fromSide(v.pair, v.mergedId!);
  for (const v of first.filter((x) => x.verdict === 'merge' && !x.setAside)) {
    const held = target.get(v.mergedId!);
    if (!held) {
      target.set(v.mergedId!, v);
      continue;
    }
    const x = shareOf(v);
    const y = shareOf(held);
    const better =
      x.contestedShare.a - y.contestedShare.a ||
      x.overlapShare.a - y.overlapShare.a ||
      (v.keeperId < held.keeperId ? 1 : -1);
    if (better > 0) target.set(v.mergedId!, v);
  }
  return first.map((v) => {
    if (v.verdict === 'merge' && target.get(v.mergedId!) === v) return v;
    // Either page merged into a third page makes this pair moot.
    // A merge whose kept page is merged on is left as it is: only the page it folds away counts.
    const candidates = v.verdict === 'merge' ? [v.mergedId!] : [v.pair.a.pageId, v.pair.b.pageId];
    const leaving = candidates.find((id) => {
      const t = target.get(id);
      return t !== undefined && t !== v;
    });
    if (leaving) {
      return {
        ...v,
        verdict: 'differentiate',
        keeperId: otherOf(v.pair, leaving),
        mergedId: null,
        basis: 'merged_elsewhere',
        mergedInto: target.get(leaving)!.keeperId,
        setAside: 'merged_elsewhere'
      };
    }
    if (v.setAside) return v;
    if (onePageNotRanking(v.pair)) return { ...v, setAside: 'one_page_not_ranking' };
    // Round 3: no differentiate verdict reaches a card or the default export (graded right 1 of 21
    // across `/review` and the Issues queue). The owner and the change stay computed, for a reader
    // of the shared-search list (F, near-miss searches); the pair is listed as checked.
    const change = differentiateChange(v.pair, v.keeperId, texts);
    return { ...v, change, setAside: 'differentiate_not_advised' };
  });
}

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

/** How many shared searches the card lists, contested first (the rest are counted). */
export const CANNIBALIZATION_CARD_SEARCHES_LISTED = 5;

/** How many shared searches the one-line action names in quotes. */
const ACTION_SEARCHES_NAMED = 2;

export function cannibalizationPath(url: string): string {
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

const q = (text: string): string => `“${text.replace(/\s+/g, ' ').trim()}”`;
const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
const count = (n: number): string => Math.round(n).toLocaleString('en-US');

/**
 * Where the pair's numbers come from: THE place for that line. With Search Console, the window.
 * On estimates, that they are estimated and that Search Console would do better (owner,
 * 2026-10-20), never a line that claims a measured number.
 */
export function cannibalizationEvidenceSourceLine(
  source: CompetingEvidenceSource,
  window: { start: string; end: string } | null
): string {
  if (source === 'search_console') {
    return window ? `Numbers from Search Console, ${window.start} to ${window.end}.` : 'Numbers from Search Console.';
  }
  return ESTIMATED_SEARCH_EVIDENCE_WARNING;
}

/**
 * One side's numbers on one shared search, as a table cell. On estimates the side's own wording
 * (`CompetingSide.wording`) with its own position and volume, named when it is not the row's
 * wording: two pages can hold one search as two wordings with two volumes.
 */
export function cannibalizationSideCell(source: CompetingEvidenceSource, side: CompetingSide, rowSearch = ''): string {
  if (source === 'search_console') {
    const pos = side.position === null ? 'not ranking' : `#${Math.round(side.position)}`;
    return `${pos} · ${plural(side.impressions ?? 0, 'impression', 'impressions')} · ${plural(side.clicks ?? 0, 'click', 'clicks')}`;
  }
  const w = side.wording ?? { search: rowSearch, position: side.position, searchVolume: side.searchVolume };
  const named = normalizeCompetingSearch(w.search) !== normalizeCompetingSearch(rowSearch) ? ` for ${q(w.search)}` : '';
  if (w.position === null) return 'not ranking (estimated)';
  return w.searchVolume
    ? `#${Math.round(w.position)}${named} · about ${count(w.searchVolume)} searches/month (both estimated)`
    : `#${Math.round(w.position)}${named} (estimated)`;
}

export interface CannibalizationSearchRow {
  search: string;
  thisPage: string;
  otherPage: string;
}

/** One pair, as the card on one of its pages shows it. */
export interface CannibalizationPairView {
  otherPageId: string;
  otherUrl: string;
  otherPath: string;
  verdict: Exclude<CannibalizationVerdict, 'keep_both'>;
  /** "Merge" or "Differentiate". */
  verdictLabel: string;
  /** What to do, naming the other page. */
  action: string;
  /** Why, from the evidence. */
  why: string;
  source: CompetingEvidenceSource;
  /** `cannibalizationEvidenceSourceLine`: the Search Console window, or the estimate warning. */
  evidenceSourceLine: string;
  /** Contested shared searches with each side's numbers, at most `CANNIBALIZATION_CARD_SEARCHES_LISTED`. */
  searches: CannibalizationSearchRow[];
  /** "They share 59 searches; 10 where both pages really show." */
  searchesLine: string;
}

function namedSearches(pair: CompetingPairShown): string {
  const contested = pair.sharedSearches.filter((s) => s.contested);
  const named = contested.slice(0, ACTION_SEARCHES_NAMED).map((s) => q(s.search));
  const rest = pair.contestedSearchCount - named.length;
  const list = named.length === 2 ? `${named[0]} and ${named[1]}` : named[0] ?? 'the shared searches';
  return rest > 0 ? `${list} (and ${plural(rest, 'other shared search', 'other shared searches')})` : list;
}

/** The contested searches named in a sentence, without brackets: "“a”, “b” and 15 other shared searches". */
function namedSearchesInline(pair: CompetingPairShown): string {
  const named = pair.sharedSearches.filter((s) => s.contested).slice(0, ACTION_SEARCHES_NAMED).map((s) => q(s.search));
  const rest = pair.contestedSearchCount - named.length;
  if (!named.length) return 'searches';
  if (rest > 0) return `${named.join(', ')} and ${plural(rest, 'other shared search', 'other shared searches')}`;
  return named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
}

/** Each page's own searches, in words; "both" only when both have some. */
function ownSearchesSentence(mine: number, theirs: number, otherPath: string): string {
  if (mine > 0 && theirs > 0) {
    return `Both pages have searches of their own: this page has ${count(mine)} that ${otherPath} does not show for; ${otherPath} has ${count(theirs)} that this page does not.`;
  }
  if (mine > 0) return `This page has ${plural(mine, 'search', 'searches')} of its own that ${otherPath} does not show for; ${otherPath} has none yet.`;
  if (theirs > 0) return `${otherPath} has ${plural(theirs, 'search', 'searches')} of its own; this page has none yet.`;
  return 'Neither page has a search of its own yet.';
}

/** Who wins, in words, from page a's side ("this page" / the other path). */
function winnerClause(v: CompetingPairVerdict, pair: CompetingPairShown, name: (id: string) => string): string {
  const keeper = v.keeperId;
  const keeperIsA = keeper === pair.a.pageId;
  const pct = (x: number | null | undefined) => `${Math.round((keeperIsA ? x ?? 0 : 1 - (x ?? 0)) * 100)}%`;
  switch (v.basis) {
    case 'clicks':
      return `${name(keeper)} gets ${pct(pair.split?.clicks)} of the clicks on the searches both pages show for`;
    case 'impressions':
      return `${name(keeper)} gets ${pct(pair.split?.impressions)} of the impressions on the searches both pages show for (neither page gets a click on them)`;
    default:
      return pair.source === 'search_console'
        ? `${name(keeper)} ranks better on the searches both pages show for`
        : `${name(keeper)} ranks better on the searches both pages are seen for (estimated positions)`;
  }
}

/** Why the owner owns the shared search, in words. */
function ownerClause(
  v: CompetingPairVerdict,
  pair: CompetingPairShown,
  row: CompetingSharedSearch | undefined,
  name: (id: string) => string,
  Name: (id: string) => string
): string {
  const keeper = v.keeperId;
  const keeperIsA = keeper === pair.a.pageId;
  const sc = pair.source === 'search_console';
  if (v.basis === 'clicks') return winnerClause(v, pair, Name);
  if (!row) return `${Name(keeper)} ranks better on the searches both pages show for`;
  const mine = keeperIsA ? row.a : row.b;
  const theirs = keeperIsA ? row.b : row.a;
  const at = (side: CompetingSide) => {
    const p = sc ? side.position : side.wording?.position ?? side.position;
    return p === null ? 'not ranking' : `#${Math.round(p)}`;
  };
  if (v.basis === 'own_search') {
    const w = theirs.wording?.search ?? row.search;
    return `${q(mine.wording?.search ?? row.search)} is the search ${name(keeper)} is for, and ${name(otherOf(pair, keeper))} ranks ${at(theirs)} for ${q(w)}${sc ? '' : ' (estimated)'}`;
  }
  return (
    `${Name(keeper)} ranks better for ${q(row.search)} when Google shows it: ${at(mine)} against ${at(theirs)}` +
    (sc ? " (Search Console's average position when each page is shown)" : ' (estimated positions)')
  );
}

/**
 * The card's view of one pair from page `pageId`. Wording is relative: "this page" is the page the
 * card is on, the other page is named by its path.
 */
export function cannibalizationPairView(
  v: CompetingPairVerdict,
  pageId: string,
  payload: CompetingPagesPayload
): CannibalizationPairView {
  const pathOf = (id: string): string => cannibalizationPath(payload.pages[id] ?? id);
  const pair = fromSide(v.pair, pageId);
  const other = pair.b;
  const otherPath = cannibalizationPath(other.url);
  const name = (id: string) => (id === pageId ? 'this page' : pathOf(id));
  const Name = (id: string) => (id === pageId ? 'This page' : pathOf(id));
  const sc = pair.source === 'search_console';
  const own = pair.ownSearches;
  const earnsWord = sc ? 'gets real impressions for' : 'ranks for';
  const searches = namedSearches(pair);

  let action: string;
  let why: string;
  if (v.verdict === 'merge') {
    const merged = v.mergedId!;
    action = merged === pageId ? `Merge this page into ${otherPath}.` : `Merge ${otherPath} into this page.`;
    if (pair.duplicate) {
      why = `The two pages are copies (the same title or H1, each its own canonical), and Google shows both for the same searches. ${winnerClause(v, pair, Name)}.`;
    } else {
      why = `${winnerClause(v, pair, Name)}, and ${name(merged)} adds nothing of its own: every search it ${earnsWord}, ${name(v.keeperId)} or another page of the site shows for too.`;
    }
  } else {
    const keeper = v.keeperId;
    const giver = otherOf(pair, keeper);
    const c = v.change;
    const its = (id: string) => (id === pageId ? "this page's" : `${pathOf(id)}'s`);
    const giverOwn = own ? (giver === pair.a.pageId ? own.a : own.b) : null;
    // A search of its own that is not the shared one reworded (it must not carry every word of it).
    const hint = c ? giverOwn?.top.find((o) => !textTargets(o.search, c.search)) : undefined;
    const ownHint = hint ? `, such as ${q(hint.search)}` : '';
    action = c
      ? `Narrow ${its(giver)} ${c.field} (${q(c.text)}) to its own searches${ownHint}, so it no longer targets ${q(c.search)}, and link from ${name(giver)} to ${name(keeper)} with those words.`
      : `${Name(keeper)} should own ${searches}.`;
    const row = c
      ? pair.sharedSearches.find((s) => s.contested && [s.search, (giver === pair.a.pageId ? s.a : s.b).wording?.search].includes(c.search))
      : pair.sharedSearches.find((s) => s.contested);
    why = `${ownerClause(v, pair, row, name, Name)}.`;
    if (own) why += ` ${ownSearchesSentence(own.a.count, own.b.count, otherPath)}`;
  }

  const contested = pair.sharedSearches.filter((s) => s.contested);
  return {
    otherPageId: other.pageId,
    otherUrl: other.url,
    otherPath,
    verdict: v.verdict,
    verdictLabel: v.verdict === 'merge' ? 'Merge' : 'Differentiate',
    action,
    why,
    source: pair.source,
    evidenceSourceLine: cannibalizationEvidenceSourceLine(payload.source, payload.window),
    searches: contested.slice(0, CANNIBALIZATION_CARD_SEARCHES_LISTED).map((s) => ({
      search: s.search,
      thisPage: cannibalizationSideCell(pair.source, s.a, s.search),
      otherPage: cannibalizationSideCell(pair.source, s.b, s.search)
    })),
    searchesLine: searchesLineOf(pair)
  };
}

/** "They share 12 searches; they really compete on 3 of them." */
function searchesLineOf(pair: CompetingPairShown): string {
  return (
    `They share ${plural(pair.sharedSearchCount, 'search', 'searches')}; they really compete on ` +
    (pair.sharedSearchCount === 1 && pair.contestedSearchCount === 1 ? 'it.' : `${pair.contestedSearchCount} of them.`)
  );
}

/** The verdicts on one page's pairs, the page as side `a`, merge first. */
export function cannibalizationVerdictsForPage(
  verdicts: readonly CompetingPairVerdict[],
  pageId: string
): CompetingPairVerdict[] {
  return verdicts
    .filter((v) => !v.setAside && (v.pair.a.pageId === pageId || v.pair.b.pageId === pageId))
    .sort((x, y) => Number(y.verdict === 'merge') - Number(x.verdict === 'merge'));
}

/** One pair of a page checked and found fine, and whether its shared searches are estimated. */
export interface CannibalizationCheckedPair {
  otherUrl: string;
  sharedSearchCount: number;
  source: CompetingEvidenceSource;
}

/** One competing pair of a page that gets no card, with why, and whether its evidence is estimated. */
export interface CannibalizationSetAsidePair {
  otherUrl: string;
  reason: string;
  source: CompetingEvidenceSource;
}

/**
 * One page's competing pairs that get no card, each with why in a sentence ("this page" is the
 * page): the everything export lists them as checked.
 */
export function cannibalizationSetAsideForPage(
  verdicts: readonly CompetingPairVerdict[],
  pageId: string,
  payload: CompetingPagesPayload
): CannibalizationSetAsidePair[] {
  const pathOf = (id: string): string => cannibalizationPath(payload.pages[id] ?? id);
  const name = (id: string) => (id === pageId ? 'this page' : pathOf(id));
  const Name = (id: string) => (id === pageId ? 'This page' : pathOf(id));
  return verdicts
    .filter((v) => v.setAside && (v.pair.a.pageId === pageId || v.pair.b.pageId === pageId))
    .map((v) => {
      const pair = fromSide(v.pair, pageId);
      return { otherUrl: pair.b.url, reason: setAsideReason(v, pair, name, Name, pathOf), source: payload.source };
    });
}

/**
 * Why a competing pair gets no card, in the words of the everything export's checked line. `pair`
 * is `v.pair` seen from the side the wording is for; `name` / `Name` say how a page is called there
 * ("this page" on a page's own export, its path in the Inventory list).
 */
function setAsideReason(
  v: CompetingPairVerdict,
  pair: CompetingPairShown,
  name: (id: string) => string,
  Name: (id: string) => string,
  pathOf: (id: string) => string
): string {
  const first = pair.sharedSearches.find((s) => s.contested);
  const search = first ? q(first.search) : 'the searches they share';
  if (v.setAside === 'merged_elsewhere') {
    const leaving = otherOf(pair, v.keeperId);
    return `${Name(leaving)} is to be merged into ${pathOf(v.mergedInto!)}, so this pair is moot`;
  }
  if (v.setAside === 'one_page_not_ranking') {
    const contested = pair.sharedSearches.filter((s) => s.contested);
    const absent = contested.every((s) => s.a.position === null) ? pair.a.pageId : pair.b.pageId;
    return `${Name(absent)} does not rank for ${search} at all, so there is nothing to split: ${name(otherOf(pair, absent))} ranks and ${name(absent)} has not won a search of its own on it`;
  }
  if (v.setAside === 'no_click_preference') {
    const tie = pair.split?.clicks === 0.5;
    const them = pair.contestedSearchCount === 1 ? 'it' : 'them';
    return (
      `both pages appear for ${namedSearchesInline(pair)} but ${tie ? `they earn the same clicks on ${them}` : `neither earns clicks on ${them}`}, ` +
      "so Google's results don't show which one to keep"
    );
  }
  return `these pages share searches (${namedSearchesInline(pair)}); we don't advise a change from this evidence`;
}

// ---------------------------------------------------------------------------
// The Inventory list: every competing pair of the domain, with its verdict
// ---------------------------------------------------------------------------

/** A page of a pair in the Inventory list. */
export interface CompetingPairListPage {
  pageId: string;
  url: string;
  path: string;
}

/** One shared search of a listed pair, each page's numbers as the card words them. */
export interface CompetingPairListSearch {
  search: string;
  first: string;
  second: string;
}

/**
 * One competing pair as the Inventory → Competing pages list shows it. Both pages are named by path
 * (the list is on no page of its own). The verdict is the card's (`competingPairVerdicts`): a merge
 * the card shows is "Merge"; every other competing pair is checked, with the reason the everything
 * export gives it. "Differentiate" is never shown client-facing (round 3), so it has no label here.
 */
export interface CompetingPairListRow {
  /** Merge: the page folded in. Checked: the pair's first page. */
  first: CompetingPairListPage;
  /** Merge: the page that stays. Checked: the pair's second page. */
  second: CompetingPairListPage;
  verdict: 'merge' | 'checked';
  /** "Merge" or "Checked, no action". */
  verdictLabel: string;
  /** Merge: "Merge /a into /b."; checked: why no change is advised, as a sentence. */
  summary: string;
  /** Checked: why the card sets the pair aside; null on a merge. */
  setAside: CannibalizationSetAside | null;
  /** Contested shared searches, at most `CANNIBALIZATION_CARD_SEARCHES_LISTED`, as the card lists them. */
  searches: CompetingPairListSearch[];
  /** "They share 12 searches; they really compete on 3 of them." */
  searchesLine: string;
}

export interface CompetingPairList {
  /** "12 pairs compete; 3 to merge." */
  headline: string;
  /** `cannibalizationEvidenceSourceLine`: the Search Console window, or the estimate warning. */
  evidenceSourceLine: string;
  /** Merges first, then the checked pairs, each group in the payload's order. */
  rows: CompetingPairListRow[];
  mergeCount: number;
}

const sentence = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;

/** Every competing pair of the domain with its verdict, for Inventory. Pure, deterministic. */
export function competingPairList(payload: CompetingPagesPayload): CompetingPairList {
  const pathOf = (id: string): string => cannibalizationPath(payload.pages[id] ?? id);
  const listPage = (side: { pageId: string; url: string }): CompetingPairListPage => ({
    pageId: side.pageId,
    url: side.url,
    path: cannibalizationPath(side.url)
  });
  const rows = competingPairVerdicts(payload.pairs, payload.texts).map((v): CompetingPairListRow => {
    const merge = v.verdict === 'merge' && !v.setAside;
    const pair = merge ? fromSide(v.pair, v.mergedId!) : v.pair;
    const contested = pair.sharedSearches.filter((s) => s.contested);
    return {
      first: listPage(pair.a),
      second: listPage(pair.b),
      verdict: merge ? 'merge' : 'checked',
      verdictLabel: merge ? 'Merge' : 'Checked, no action',
      summary: merge
        ? `Merge ${pathOf(pair.a.pageId)} into ${pathOf(pair.b.pageId)}.`
        : sentence(setAsideReason(v, pair, pathOf, pathOf, pathOf)),
      setAside: merge ? null : v.setAside,
      searches: contested.slice(0, CANNIBALIZATION_CARD_SEARCHES_LISTED).map((s) => ({
        search: s.search,
        first: cannibalizationSideCell(pair.source, s.a, s.search),
        second: cannibalizationSideCell(pair.source, s.b, s.search)
      })),
      searchesLine: searchesLineOf(pair)
    };
  });
  const merges = rows.filter((r) => r.verdict === 'merge');
  const checked = rows.filter((r) => r.verdict !== 'merge');
  const headline = rows.length
    ? `${plural(rows.length, 'pair competes', 'pairs compete')}; ${count(merges.length)} to merge.`
    : 'No two pages of this site compete for the same searches.';
  return {
    headline,
    evidenceSourceLine: cannibalizationEvidenceSourceLine(payload.source, payload.window),
    rows: [...merges, ...checked],
    mergeCount: merges.length
  };
}

/** The pairs of one page checked and found fine. */
export function cannibalizationCheckedForPage(
  payload: CompetingPagesPayload | null,
  pageId: string
): CannibalizationCheckedPair[] {
  if (!payload) return [];
  return payload.checked
    .filter((c) => c.a === pageId || c.b === pageId)
    .map((c) => ({
      otherUrl: payload.pages[c.a === pageId ? c.b : c.a] ?? '',
      sharedSearchCount: c.sharedSearchCount,
      source: payload.source
    }));
}
